// Pure dot-path reader / writer / deleter over the score-spec definition buffer.
//
// Every write in the feature routes through here for one reason: `useSpecEditor`'s isDirty is a
// `JSON.stringify` equality check, which is key-order sensitive. `setAt` updates an existing key
// IN PLACE (object spread preserves insertion order for keys that already exist) and only appends
// genuinely new keys, so opening a spec and editing one field can never flip isDirty on a
// semantically identical buffer.
//
// Numeric path segments index arrays: 'document_set.documents.doc_types.0.latest_n'.

const parsePath = (path) => (Array.isArray(path) ? path : String(path).split('.')).filter((s) => s !== '');

const isIndex = (segment) => /^\d+$/.test(String(segment));

export const getAt = (obj, path) =>
  parsePath(path).reduce((acc, key) => (acc === null || acc === undefined ? undefined : acc[key]), obj);

export const hasAt = (obj, path) => {
  const keys = parsePath(path);
  let cursor = obj;
  for (let i = 0; i < keys.length; i += 1) {
    if (cursor === null || typeof cursor !== 'object') return false;
    if (!Object.prototype.hasOwnProperty.call(cursor, keys[i])) return false;
    cursor = cursor[keys[i]];
  }
  return keys.length > 0;
};

// Returns a new root. Missing intermediates are created only on the way to a leaf that is actually
// being set — an array if the next segment is numeric, an object otherwise.
export const setAt = (obj, path, value) => {
  const keys = parsePath(path);
  if (keys.length === 0) return value;

  const [head, ...rest] = keys;
  const childOf = (current) =>
    rest.length === 0
      ? value
      : setAt(current === undefined || current === null ? (isIndex(rest[0]) ? [] : {}) : current, rest, value);

  if (Array.isArray(obj)) {
    const next = obj.slice();
    next[Number(head)] = childOf(obj[Number(head)]);
    return next;
  }

  const base = obj !== null && typeof obj === 'object' ? obj : {};
  return { ...base, [head]: childOf(base[head]) };
};

// Removes the leaf at `path`. Absent leaves are a no-op that returns the SAME reference, so an
// unsetAt on something that was never there cannot make the buffer dirty.
export const unsetAt = (obj, path) => {
  const keys = parsePath(path);
  if (keys.length === 0) return obj;

  const [head, ...rest] = keys;

  if (Array.isArray(obj)) {
    const idx = Number(head);
    if (idx < 0 || idx >= obj.length) return obj;
    if (rest.length === 0) return obj.filter((_, i) => i !== idx);
    const nextChild = unsetAt(obj[idx], rest);
    if (nextChild === obj[idx]) return obj;
    const next = obj.slice();
    next[idx] = nextChild;
    return next;
  }

  if (obj === null || typeof obj !== 'object') return obj;
  if (!Object.prototype.hasOwnProperty.call(obj, head)) return obj;

  if (rest.length === 0) {
    const next = {};
    Object.keys(obj).forEach((k) => {
      if (k !== head) next[k] = obj[k];
    });
    return next;
  }

  const nextChild = unsetAt(obj[head], rest);
  if (nextChild === obj[head]) return obj;
  return { ...obj, [head]: nextChild };
};

// Every dotted leaf path in a plain object/array tree, in document order. Used by diffPaths.
export const leafPaths = (value, prefix = '') => {
  if (value === null || typeof value !== 'object') return [prefix];
  if (Array.isArray(value)) {
    if (value.length === 0) return [prefix];
    return value.reduce((acc, item, i) => acc.concat(leafPaths(item, prefix ? `${prefix}.${i}` : String(i))), []);
  }
  const keys = Object.keys(value);
  if (keys.length === 0) return [prefix];
  return keys.reduce((acc, key) => acc.concat(leafPaths(value[key], prefix ? `${prefix}.${key}` : key)), []);
};
